import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { campaignsApiPath } from './apiPath';
import { SeedingContentChannelSchema } from './getSeedingMeRecruitment.api';

export const RecruitmentSortSchema = z.enum(['LATEST', 'SELECTION_PROBABILITY', 'DEADLINE_SOON']);
export type RecruitmentSort = z.infer<typeof RecruitmentSortSchema>;

const GetRecruitmentCampaignsRequestSchema = z
  .object({
    categoryNo: z.string().optional(),
    likedBrandOnly: z.boolean().optional(),
    sort: RecruitmentSortSchema.optional(),
    page: z.number().int().min(1).max(10000).optional(),
    size: z.number().int().min(1).max(100).optional(),
  })
  .describe('GetRecruitmentCampaignsRequestSchema');

export type GetRecruitmentCampaignsRequest = z.infer<typeof GetRecruitmentCampaignsRequestSchema>;

const CampaignGoodsItemSchema = z
  .object({
    goodsNo: z.number(),
    goodsName: z.string(),
    imageUrl: z.string(),
    price: z.number(),
    finalPrice: z.number().nullable().optional(),
  })
  .describe('CampaignGoodsItemSchema');

const CampaignGoodsSlotSchema = z
  .object({
    slotNo: z.number(),
    goods: z.array(CampaignGoodsItemSchema),
  })
  .describe('CampaignGoodsSlotSchema');

const RecruitmentCampaignItemSchema = z
  .object({
    campaignId: z.number(),
    brandId: z.string(),
    brandName: z.string(),
    goodsName: z.string(),
    goodsImage: z.string(),
    goodsPrice: z.number(),
    recruitmentEndAt: z.string(),
    snsChannels: z.array(SeedingContentChannelSchema),
    goodsSlots: z.array(CampaignGoodsSlotSchema),
  })
  .describe('RecruitmentCampaignItemSchema');

const GetRecruitmentCampaignsResponseDataSchema = z.object({
  list: z.array(RecruitmentCampaignItemSchema),
});

const GetRecruitmentCampaignsResponseSchema = createApiResponseSchema(
  GetRecruitmentCampaignsResponseDataSchema,
);

export type GetRecruitmentCampaignsResponse = z.infer<typeof GetRecruitmentCampaignsResponseSchema>;

export const getRecruitmentCampaigns = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetRecruitmentCampaignsRequestSchema),
    async (params: GetRecruitmentCampaignsRequest): Promise<GetRecruitmentCampaignsResponse> =>
      executeStandardRequest(async () => {
        const response = await privateHttpClient.get<GetRecruitmentCampaignsResponse>(
          urlJoin(campaignsApiPath.getRecruitment),
          params,
        );
        return response;
      }),
    validateApiResponse(GetRecruitmentCampaignsResponseSchema),
  ),
);
